import { Hash } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SAFE = /^[A-Za-z0-9 _\-/.#]*$/;

export const formatSeqNo = (f, k = "inv") => `${f[`${k}_prefix`] || ""}${String(parseInt(f[`${k}_next`], 10) || 0).padStart(parseInt(f[`${k}_pad`], 10) || 0, "0")}${f[`${k}_suffix`] || ""}`;
export const numberingInvalid = (f) => ["inv_prefix", "inv_suffix", "ld_prefix", "ld_suffix"].some((k) => !SAFE.test(f[k] || ""));

const Box = ({ id, label, hint, children }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    {children}
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

// Carrier-controlled numbers: prefix + zero-padded sequence + suffix. k = "inv" (invoices) or "ld" (loads).
const Numbering = ({ f, set, k, title, noun, testId }) => {
  const bad = (x) => !SAFE.test(f[`${k}_${x}`] || "");
  const inp = (x, extra) => (
    <Input id={`${k}_${x}`} value={f[`${k}_${x}`]} onChange={(e) => set(`${k}_${x}`)(e.target.value)} data-testid={`settings-${k}_${x}`} {...extra}
      className={`mt-1 ${(x === "prefix" || x === "suffix") && bad(x) ? "border-rose-400" : ""}`} />
  );
  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50/60 p-5" data-testid={testId}>
      <div className="flex items-center gap-2 font-semibold text-slate-900"><Hash className="w-4 h-4 text-[#002FA7]" /> {title}</div>
      <p className="text-sm text-slate-500 mt-1">Match your own books. Changes apply to new {noun}s only; existing {noun}s keep their numbers.</p>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Box id={`${k}_prefix`} label="Prefix" hint="Optional, e.g. NAV- or 2025-">{inp("prefix", { maxLength: 20, placeholder: "None" })}</Box>
        <Box id={`${k}_next`} label={`Next ${noun} number`} hint={`The next ${noun} gets this number`}>{inp("next", { type: "number", min: 1 })}</Box>
        <Box id={`${k}_pad`} label="Minimum digits" hint="0 = no padding; 4 turns 7 into 0007">{inp("pad", { type: "number", min: 0, max: 10 })}</Box>
        <Box id={`${k}_suffix`} label="Suffix" hint="Optional, e.g. -A">{inp("suffix", { maxLength: 20, placeholder: "None" })}</Box>
      </div>
      {(bad("prefix") || bad("suffix")) && <p className="mt-2 text-xs text-rose-600" data-testid={`settings-${k}-format-error`}>Use letters, numbers, spaces and - _ / . # only.</p>}
      <div className="mt-4 text-sm text-slate-600">Next {noun} will be <span className="font-mono font-semibold text-slate-900 bg-white border border-slate-200 rounded px-2 py-0.5" data-testid={`settings-${k}-preview`}>{formatSeqNo(f, k)}</span></div>
    </div>
  );
};

export const InvoiceNumbering = (p) => <Numbering {...p} k="inv" title="Invoice numbering" noun="invoice" testId="settings-invoice-numbering" />;
export const LoadNumbering = (p) => <Numbering {...p} k="ld" title="Load numbering" noun="load" testId="settings-load-numbering" />;
